/**
 * WordPress dependencies
 */
import { Button } from '@safe-wordpress/components';
import { _x, _nx, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { clsx } from 'clsx';
import type { FinishedExperimentSummary } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { type Results, useResults } from '../../hooks';

export type RecentlyFinishedExperimentProps = {
	readonly experiment: FinishedExperimentSummary;
};

export function RecentlyFinishedExperiment( {
	experiment,
}: RecentlyFinishedExperimentProps ): JSX.Element | null {
	const results = useResults( experiment.id );

	if ( 'error' === results.status ) {
		return null;
	}

	const isLoading = 'loading' === results.status;
	const isWinner = 'ready' === results.status && results.improvement > 0;
	const isNeutral = 'ready' === results.status && results.improvement === 0;

	return (
		<li className="nab-recently-finished-experiment">
			<div
				className={ clsx( {
					'nab-recently-finished-experiment__status': true,
					'nab-recently-finished-experiment__status--positive':
						isWinner,
					'nab-recently-finished-experiment__status--neutral':
						isNeutral,
					'nab-recently-finished-experiment__status--negative':
						! isLoading && ! isWinner && ! isNeutral,
					'nab-experiment__loading-value': isLoading,
				} ) }
				aria-hidden="true"
			>
				{
					// eslint-disable-next-line no-nested-ternary
					isLoading || isNeutral ? '–' : isWinner ? '✓' : '×'
				}
			</div>

			<div className="nab-recently-finished-experiment__content">
				<strong className="nab-recently-finished-experiment__name">
					{ experiment.name }
				</strong>
				<span className="nab-recently-finished-experiment__date">
					{ formatRelativeDate( experiment.endDate ) }
				</span>
			</div>

			<div className="nab-recently-finished-experiment__metric">
				<strong
					className={ clsx( {
						'nab-recently-finished-experiment__improvement': true,
						'nab-recently-finished-experiment__improvement--positive':
							isWinner,
						'nab-recently-finished-experiment__improvement--neutral':
							isNeutral,
						'nab-recently-finished-experiment__improvement--negative':
							! isWinner && ! isNeutral,
						'nab-experiment__loading-value': isLoading,
					} ) }
				>
					{ formatImprovement( results ) }
				</strong>
				<span
					className={ clsx( {
						'nab-recently-finished-experiment__label': true,
					} ) }
				>
					{
						// eslint-disable-next-line no-nested-ternary
						isLoading
							? ''
							: // eslint-disable-next-line no-nested-ternary
							isNeutral
							? 'No change'
							: isWinner
							? 'Improvement'
							: 'Change'
					}
				</span>
			</div>

			<Button
				className="nab-recently-finished-experiment__button"
				variant="secondary"
				href={ experiment.resultUrl }
				icon="chart-bar"
				aria-label={ `View results for ${ experiment.name }` }
			/>
		</li>
	);
}

function formatImprovement( results: Results ): string {
	if ( 'error' === results.status ) {
		return '';
	}

	if ( 'loading' === results.status ) {
		return '█';
	}

	const value = results.improvement;
	if ( 0 === value ) {
		return '–';
	}

	const sign = value > 0 ? '↑' : '↓';
	return `${ sign }${ Math.abs( value ).toFixed( 1 ) }%`;
}

function formatRelativeDate( value: string ): string {
	const date = new Date( value );
	const days = Math.max(
		0,
		Math.round( ( Date.now() - date.getTime() ) / 86400000 )
	);

	if ( ! days ) {
		return _x( 'Finished today', 'text', 'nelio-ab-testing' );
	}

	if ( 1 === days ) {
		return _x( 'Finished yesterday', 'text', 'nelio-ab-testing' );
	}

	if ( days < 30 ) {
		return sprintf(
			/* translators: %d: Number of days. */
			_nx(
				'Finished %d day ago',
				'Finished %d days ago',
				days,
				'text',
				'nelio-ab-testing'
			),
			days
		);
	}

	if ( days < 90 ) {
		const weeks = Math.round( days / 7 );
		return sprintf(
			/* translators: %d: Number of weeks. */
			_nx(
				'Finished %d week ago',
				'Finished %d weeks ago',
				weeks,
				'text',
				'nelio-ab-testing'
			),
			weeks
		);
	}

	const months = Math.round( days / 30 );
	return sprintf(
		/* translators: %d: Number of months. */
		_nx(
			'Finished %d month ago',
			'Finished %d months ago',
			months,
			'text',
			'nelio-ab-testing'
		),
		months
	);
}
